// NotificacionesUsuario.js — Merge Solponiente + SectorBXII
import React, { useEffect, useState } from "react";
import { Card, Row, Col, Spinner, Button } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import FirmaNotificacion from "../FirmaNotificacion";
import Header from "../Components/Header";

const NotificacionesUsuario = ({ onActualizarLeidas }) => {
  const [notificaciones, setNotificaciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mostrarModal, setMostrarModal] = useState(false);
  const [notificacionParaFirmar, setNotificacionParaFirmar] = useState(null);
  const [filtro, setFiltro] = useState("todas");

  const usuarioId = localStorage.getItem("id");

  useEffect(() => {
    fetchNotificaciones();
  }, [usuarioId]);

  const fetchNotificaciones = async () => {
    try {
      const response = await fetch(
        `https://oficinavirtual-solponiente.com/api/notificaciones/usuarios/${usuarioId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      if (!response.ok) throw new Error("Error al obtener las notificaciones");
      const data = await response.json();

      // Para cada notificación con SolicitudID, obtener la descripción de la solicitud
      const dataConSolicitudes = await Promise.all(
        data.map(async (noti) => {
          if (noti.SolicitudID) {
            try {
              const res = await fetch(
                `https://oficinavirtual-solponiente.com/api/solicitudes/${noti.SolicitudID}`,
                {
                  headers: {
                    Authorization: `Bearer ${localStorage.getItem("token")}`,
                  },
                }
              );
              if (res.ok) {
                const solicitud = await res.json();
                return { ...noti, SolicitudDescripcion: solicitud.Descripcion };
              }
            } catch (e) {
              console.error("Error obteniendo solicitud:", e);
            }
          }
          return { ...noti, SolicitudDescripcion: null };
        })
      );

      setNotificaciones(dataConSolicitudes);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const abrirModalFirma = (notificacion) => {
    const fechaActual = new Date();
    const formatDate = (date) => date.toISOString().split("T")[0];
    const formatDateTime = (date) =>
      date.toISOString().replace("T", " ").split(".")[0];

    const notificacionConFechas = {
      ...notificacion,
      FechaEnvio: formatDate(new Date(notificacion.FechaEnvio)),
      FechaFirma: formatDate(fechaActual),
      FechaRecepcion: formatDateTime(fechaActual),
    };

    setNotificacionParaFirmar(notificacionConFechas);
    setMostrarModal(true);
  };

  const cerrarModalFirma = () => {
    setMostrarModal(false);
    setNotificacionParaFirmar(null);
    fetchNotificaciones(); // refresca la lista tras firmar
    if (typeof onActualizarLeidas === "function") onActualizarLeidas();
  };

  const handleDownload = (doc) => {
    const binaryData = atob(doc);
    const blob = new Blob([
      new Uint8Array(binaryData.split("").map((char) => char.charCodeAt(0))),
    ]);
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "documento.pdf";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="d-flex flex-column">
      <Header />
      <h4 className="mt-4 mb-2">
        <i className="bi bi-envelope text-primary"></i> Notificaciones
      </h4>
      <p className="text-muted mb-4">
        En esta sección puede consultar y firmar sus notificaciones recibidas a
        través de la Oficina Virtual.
      </p>

      {/* Filtros */}
      <div className="mb-3">
        <Button
          variant={filtro === "todas" ? "primary" : "outline-primary"}
          size="sm"
          className="me-2"
          onClick={() => setFiltro("todas")}
        >
          Todas
        </Button>
        <Button
          variant={filtro === "firmadas" ? "primary" : "outline-primary"}
          size="sm"
          className="me-2"
          onClick={() => setFiltro("firmadas")}
        >
          Firmadas
        </Button>
        <Button
          variant={filtro === "pendientes" ? "primary" : "outline-primary"}
          size="sm"
          onClick={() => setFiltro("pendientes")}
        >
          Pendientes
        </Button>
      </div>

      {loading ? (
        <div className="text-center">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Cargando...</span>
          </Spinner>
          <p className="mt-3">Cargando notificaciones...</p>
        </div>
      ) : error ? (
        <p className="text-danger text-center">{error}</p>
      ) : notificaciones.length === 0 ? (
        <p className="text-center">No tienes notificaciones.</p>
      ) : (
        <Row>
          {notificaciones
            .filter((n) => {
              if (filtro === "firmadas") return n.Firmado;
              if (filtro === "pendientes") return !n.Firmado;
              return true;
            })
            .map((notificacion) => (
              <Col
                md={12}
                lg={12}
                key={notificacion.NotificacionID}
                className="mb-4"
              >
                <Card className="shadow-sm bg-white h-100">
                  <Card.Body>
                    <Card.Title className="text-primary fs-6">
                      Notificación
                    </Card.Title>
                    <div className="mb-2 text-muted">
                      Recibida el{" "}
                      {new Date(notificacion.FechaEnvio).toLocaleDateString(
                        "es-ES",
                        { year: "numeric", month: "long", day: "numeric" }
                      )}
                    </div>

                    {notificacion.SolicitudDescripcion && (
                      <div className="mb-2">
                        <strong>Solicitud relacionada:</strong>{" "}
                        {notificacion.SolicitudDescripcion}
                      </div>
                    )}

                    <Card.Text>
                      <div
                        className="mt-2"
                        style={{ color: notificacion.Firmado ? "inherit" : "red" }}
                      >
                        {notificacion.Firmado ? (
                          <p>{notificacion.Descripcion}</p>
                        ) : (
                          "Esta notificación requiere firma electrónica para visualizarse."
                        )}
                      </div>
                    </Card.Text>

                    {notificacion.Firmado && notificacion.Documentos && (
                      <div className="mt-2">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleDownload(notificacion.Documentos)}
                        >
                          <i className="bi bi-download"></i>{" "}
                          <u>Ver documento</u>
                        </Button>
                      </div>
                    )}
                  </Card.Body>

                  <Card.Footer className="d-flex justify-content-between">
                    {notificacion.Firmado ? (
                      <Button variant="primary" disabled>
                        Notificación firmada
                      </Button>
                    ) : (
                      <Button
                        variant="primary"
                        onClick={() => abrirModalFirma(notificacion)}
                      >
                        <i className="bi bi-pencil-square me-2"></i> Firmar
                        notificación
                      </Button>
                    )}
                  </Card.Footer>
                </Card>
              </Col>
            ))}
        </Row>
      )}

      {mostrarModal && notificacionParaFirmar && (
        <FirmaNotificacion
          notificacion={notificacionParaFirmar}
          onFirmada={cerrarModalFirma}
        />
      )}
    </div>
  );
};

export default NotificacionesUsuario;
